// Filename: DataBIRequestsTable.tsx
import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List, Tooltip } from "@mui/material";
import RectangleIcon from "@mui/icons-material/Rectangle";
import moment from "moment";
import { useNavigate } from "react-router-dom";

// Interface for the props of this component
interface DataBIRequestsTableProps {
    requests: any[]; // Expects the ProcessedDataBIRequest structure
}

const DataBIRequestsTable: React.FC<DataBIRequestsTableProps> = ({
    requests,
}: DataBIRequestsTableProps) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    // Group statuses for styling
    const pendingStatuses = ['pending', 'submitted', 'provisioning'];
    const activeStatuses = ['active', 'approved', 'success'];
    const rejectedStatuses = ['rejected', 'failed'];

    return (
        <List sx={{ bgcolor: "#fafafa" }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        <Grid item xs={2}>
                            <Typography fontSize="12px">SERVICE NAME</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography fontSize="12px">APPLICATION</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px">OWNER</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px">REQUESTED ON</Typography>
                        </Grid>
                        <Grid textAlign="start" item xs={2}>
                            <Typography fontSize="12px">ACTIONED BY / REASON</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px">ACTIONED ON</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </ListItem>
            {requests.map((req: any, index: number) => (
                <ListItem
                    key={req.id}
                    sx={{
                        border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
                        mt: 1,
                        cursor: "pointer",
                        bgcolor: activeStatuses.includes(req.status)
                                ? "#FFFFFF"
                                : rejectedStatuses.includes(req.status)
                                ? "#F0443810"
                                : "#FDB02210", // Default to pending color
                        borderRadius: "8px",
                    }}
                    onClick={() => {
                        // Navigate to the Data BI request details page
                        navigate(`/datasolutions/requests/data-bi/${req.id}`);
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => {
                        setCardHoverIndex(null);
                    }}
                >
                    <Box width="100%" sx={{}}>
                        <Grid container direction="row" alignItems="center" justifyContent="space-between">
                            <Grid item xs={2}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <RectangleIcon
                                        sx={{
                                            color: activeStatuses.includes(req.status)
                                                    ? "#12B76A"
                                                    : rejectedStatuses.includes(req.status)
                                                    ? "#F04438"
                                                    : "#FDB022",
                                            bgcolor: activeStatuses.includes(req.status)
                                                    ? "#12B76A"
                                                    : rejectedStatuses.includes(req.status)
                                                    ? "#F04438"
                                                    : "#FDB022",
                                            width: "4px",
                                            marginRight: "8px",
                                        }}
                                    />
                                    <Typography fontSize="14px" fontFamily="Gilroy-Medium">
                                        {req.serviceName}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                    {req.applicationName || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Avatar
                                        src={req.ownerAvatar} // Will be undefined, showing fallback
                                        alt={req.ownerName}
                                        sx={{ marginRight: "8px", height: 30, width: 30 }}
                                    >
                                     {/* Fallback to initials if no src */}
                                     {req.ownerName ? req.ownerName.charAt(0).toUpperCase() : ''}
                                    </Avatar>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {req.ownerName || 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {req.createdAt ? moment(req.createdAt).format("DD/MM/YYYY | hh:mm A") : 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                {req.status !== "pending" && (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {req.approverName && (
                                            <Avatar
                                                alt={req.approverName}
                                                src={req.approverAvatar} // <-- ADD THIS LINE
                                                sx={{ marginRight: "8px", height: 30, width: 30 }}
                                            >
                                                {req.approverName.charAt(0).toUpperCase()}
                                            </Avatar>
                                            )}
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" mr={1}>
                                            {req.approverName || 'N/A'}
                                        </Typography>
                                        {rejectedStatuses.includes(req.status) && req.reasonForRejection && (
                                            <Tooltip title={`Reason: ${req.reasonForRejection}`}>
                                                <img src="/assets/info.svg" alt="info" />
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={2}>
                                {req.actionedAt && !pendingStatuses.includes(req.status) && (
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                            {moment(req.actionedAt).format("DD/MM/YYYY | hh:mm A")}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default DataBIRequestsTable;